import { Form, Formik, type FormikHelpers } from 'formik';
import { ldaNumberValidator, stringValidator } from 'gcs-common/helper/yupValidationHelper';
import {
  getCreateErpCustomerDefaultErrorMessage,
  getCreateErpCustomerErrorMessage,
} from 'gcs-common/slices/administration/erpCustomers/erpCustomersSelectors';
import {
  resetCreateErpCustomerDefaultErrorMessage,
  resetCreateErpCustomerErrorMessage,
} from 'gcs-common/slices/administration/erpCustomers/erpCustomersSlice';
import { createErpCustomer } from 'gcs-common/slices/administration/erpCustomers/erpCustomersThunks';
import { getSelectedHouseLdaNumbers } from 'gcs-common/slices/administration/houses/housesSelectors';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { showFailureNotification } from '../../../../slices/notifications/ShowNotificationThunk';
import Button from '../../../Button/Button';
import FormikInput from '../../../FormikInput/FormikInput';
import FormikSelect from '../../../FormikSelect/FormikSelect';
import styles from './styles.module.scss';

const ErpCustomerValidationSchema = Yup.object().shape({
  friendlyName: stringValidator,
  customerNumber: Yup.string()
    .matches(
      /^((TEON01_TE_+)|([0-9A-Za-z]{6})|([0-9A-Za-z]{10}))$/,
      'Die Kundennummer muss entweder aus 6 oder 10 Zeichen bestehen.',
    )
    .required('Erforderlich'),
  zip: Yup.string().matches(/^[0-9]{5}$/, 'Die Postleitzahl muss 5-stellig sein'),
  ldaNumber: ldaNumberValidator,
});

const initialData = {
  friendlyName: '',
  description: '',
  shortName: '',
  customerNumber: '',
  ldaNumber: [],
  lsp: '',
  street: '',
  zip: '',
  city: '',
};

interface CustomerCreateForm {
  friendlyName: string;
  description: string;
  shortName: string;
  customerNumber: string;
  ldaNumber: string[];
  lsp: string;
  street: string;
  zip: string;
  city: string;
}

interface ErpCustomerCreateProps {
  onCancel: VoidFunction;
  onSuccess: VoidFunction;
}

const ErpCustomerCreate = (props: ErpCustomerCreateProps) => {
  const { onCancel, onSuccess } = props;
  const dispatch = useDispatch();
  const errorMessage = useSelector(getCreateErpCustomerErrorMessage);
  const defaultErrorMessage = useSelector(getCreateErpCustomerDefaultErrorMessage);
  const houseLDAs: string[] = useSelector(getSelectedHouseLdaNumbers);

  useEffect(() => {
    if (defaultErrorMessage) {
      dispatch(showFailureNotification(defaultErrorMessage, dispatch(resetCreateErpCustomerDefaultErrorMessage())));
    }
  }, [defaultErrorMessage, dispatch]);

  useEffect(() => {
    dispatch(resetCreateErpCustomerErrorMessage());
    dispatch(resetCreateErpCustomerDefaultErrorMessage());
  }, [dispatch]);

  const onCustomerIdChanged = useCallback(() => {
    if (errorMessage) {
      dispatch(resetCreateErpCustomerErrorMessage());
    }
  }, [dispatch, errorMessage]);

  const submit = useCallback(
    async (values: CustomerCreateForm, { setSubmitting }: FormikHelpers<CustomerCreateForm>) => {
      const result = await dispatch(
        // @ts-expect-error redux
        createErpCustomer({
          erpCustomer: {
            ...values,
          },
        }),
      );
      setSubmitting(false);
      // @ts-expect-error redux
      if (!result.error) {
        onSuccess();
      }
    },
    [dispatch, onSuccess],
  );

  return (
    <Formik<CustomerCreateForm>
      initialValues={initialData}
      validationSchema={ErpCustomerValidationSchema}
      onSubmit={submit}
    >
      {({ isSubmitting }) => (
        <Form className={styles.column}>
          <div className={styles.row}>
            <FormikInput label="Name*" name="friendlyName" />
            <FormikInput label="Kurzname" name="shortName" />
          </div>

          <div className={styles.row}>
            {/* Note: using onChange/onFocus might clash with formik-validation */}
            <FormikInput label="Kundennummer*" type="text" name="customerNumber" onKeyUp={onCustomerIdChanged} />
            <FormikSelect
              options={houseLDAs.map((lda) => ({ key: lda, value: lda }))}
              label="LDA*"
              name="ldaNumber"
              valueKey="key"
              labelKey="value"
            />
          </div>

          {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}

          <div className={styles.row}>
            <FormikInput label="LSP" name="lsp" />
          </div>

          <div className={styles.row}>
            <FormikInput label="Beschreibung" name="description" />
          </div>

          <div className={styles.row}>
            <FormikInput label="Straße und Hausnummer" name="street" />
          </div>

          <div className={styles.row}>
            <FormikInput label="PLZ" name="zip" />
            <FormikInput label="Stadt" name="city" />
          </div>

          <div className={styles.buttonRow}>
            <Button className={styles.modalButtonSecondary} type="button" disabled={isSubmitting} onClick={onCancel}>
              Abbrechen
            </Button>
            <Button className={styles.modalButton} type="submit" disabled={isSubmitting}>
              Speichern
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default ErpCustomerCreate;
export { ErpCustomerValidationSchema };
